import React from "react";

import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import CustomAutoCompleteFilter from "../../lib/components/customdatagridfilter/CustomFilter";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Toolbar from "@material-ui/core/Toolbar";
import CheckIcon from "@material-ui/icons/Check";

import { getFinancialYearSelected } from "../../lib/api/week";
import { arrivalDumpDataExcel } from "../../lib/api/arrival";

import Grid from "../../lib/components/grid";
import { GridColumns } from "./arrivalgridsetup";

import ArrivalGridActionDialog from "./arrivalgridactiondialog";
import Confirmation from "../../lib/components/confirmation";
import { DialogInformation } from "../../lib/components/dialoginformation";

import { SnackContext } from "../../lib/context/SnackContext";
import { Row } from "react-data-grid";
import { GenerateErrorMessage } from "../../lib/helpers/string_methods";
import { DocumentsType } from "../../lib/components/DocumentsButton";
import { RedButton } from "../../lib/components/ColorButtons";
import { updateArrivalData } from "../../lib/api/arrivaldata";
import { getActiveDefects } from "../../lib/api/defects";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(1),
      width: "100%",
      height: "100%",
    },
    inline: {
      display: "inline",
      position: "absolute",
      marginTop: "7px",
      marginLeft: "7px",
    },
    greenRow: {
      color: "green",
    },
    normalRow: {
      color: "black",
    },
    toolbarWrapper: {
      position: "relative",
    },
    toolbar: {
      position: "absolute",
      left: 0,
      width: "100%",
      gap: `${theme.spacing(2)}px`,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
  });

type ArrivalsGridProps = {
  data: any[];
  loading: boolean;
  handleEdit(container: string, producerid: string, salesref: string, arrival_id: number, status: number, claim: boolean): void;
  handleRemove(arrival_id: number): void;
  handleReload(): void;
  hideCompleted?: boolean;
  captureNote?: boolean;
} & WithStyles<typeof styles>;

class ArrivalsGridUnstyled extends React.Component<ArrivalsGridProps, {}> {
  state = {
    classes: undefined,
    handleEdit: undefined,
    handleRemove: undefined,
    hideCompleted: undefined,
    dataLoading: false,
    captureNote: undefined,
    bePatient: false,
    dataChanged: [],
    data: [],
    defects: [],
  };

  constructor(props: ArrivalsGridProps) {
    super(props);

    this.state = {
      dataLoading: false,
      bePatient: false,
      dataChanged: [],
      data: props.data || [],
      hideCompleted: props.hideCompleted,
      captureNote: props.captureNote,
      classes: props.classes,
      handleEdit: props.handleEdit,
      handleRemove: props.handleRemove,
      defects: [],
    };
  }

  static contextType = SnackContext;
  context!: React.ContextType<typeof SnackContext>;

  loadActiveDefects = async () => {
    const defects = await getActiveDefects();
    this.setState({ defects });
  };

  componentDidMount(): void {
    // localStorage.removeItem("arrivalsgridsetupLayout");
    this.loadActiveDefects();
  }

  componentDidUpdate(prevProps: Readonly<ArrivalsGridProps>) {
    if (prevProps.data !== this.props.data) {
      this.setState({ data: this.props.data });
    }
  }

  GridColumnsOverride = (data, filters, arrangement, columnsWidth, _) => {
    // collect all defect keys that actually have data in any row
    const activeDefectKeys = new Set();

    const gridData = data && data.length > 0 ? data : this.state.data;

    (gridData || []).forEach((row) => {
      this.state.defects.forEach((d) => {
        const key = d.defect.toLowerCase().replaceAll(/[^a-zA-Z]/g, "_");
        if (row[key]) {
          activeDefectKeys.add(key);
        }
      });
    });

    const columns = this.state.defects.reduce((acc, d) => {
      const key = d.defect.toLowerCase().replaceAll(/[^a-zA-Z]/g, "_");

      // only add column if it has active data
      if (activeDefectKeys.has(key)) {
        acc.push({
          key,
          name: d.defect,
          width: 50,
          filterable: true,
          formatter: ({ row }) =>
            row[key] ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <CheckIcon style={{ color: "green" }} />
              </div>
            ) : (
              <></>
            ),
          filterRenderer: (e) => {
            return <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={key} data={data} section="arrivalsgridsetup" />;
          },
        });
      }

      return acc;
    }, []);

    return GridColumns(data, filters, arrangement, columnsWidth, this.state.handleEdit, this.state.handleRemove, this.props.loading, columns);
  };

  rowRenderer = (props, classes) => {
    const { row } = props;
    const colorStyle = row.arrival_id != 0 ? classes.greenRow : classes.normalRow;

    return <Row {...props} className={colorStyle} />;
  };

  handleHideCompleted = () => {
    this.setState({ hideCompleted: !this.state.hideCompleted });
  };

  handleGridRowDoubleClick = (arrivalid) => {
    if (arrivalid != 0) {
      this.setState({ captureNote: arrivalid });
    }
  };

  handleGridActionClose = () => {
    this.setState({ captureNote: undefined }, () => {
      this.props.handleReload();
    });
  };

  handleBePatientClose = () => {
    this.setState({ bePatient: false });
  };

  handlePrint = async (data, columns, excludeConsignee: number) => {
    try {
      this.setState({ bePatient: true, dataLoading: true, loading: true });

      const ignorableCols = ["actions"];
      let gridColumns = columns.reduce((arr, col) => {
        if (col.key === "status_description") {
          arr.push({ key: "status_description", header: "Status" });
        } else if (!ignorableCols.includes(col.key)) {
          arr.push({ key: col.key, header: col.name });
        }
        return arr;
      }, []);

      const exportData = data.map((row) => {
        if (excludeConsignee) {
          delete row.consigneeid;
          delete row.consignee;
        }
        return {
          ...row,
          sale_arrived: row.sale_arrived == 1 ? "✓" : "",
          sale_collected: row.sale_collected == 1 ? "✓" : "",
        };
      });

      if (excludeConsignee) {
        gridColumns = gridColumns.filter((col) => col.key !== "consigneeid" && col.key !== "consignee");
        await arrivalDumpDataExcel({ data: exportData, gridColumns }, 1, getFinancialYearSelected());
        return;
      }
      await arrivalDumpDataExcel({ data: exportData, gridColumns }, 0, getFinancialYearSelected());
    } catch (error) {
      const err = GenerateErrorMessage(error, "failed to retrieve data");
      this.context.updateSnack({ show: true, color: "red", message: err });
    }
    this.setState({ dataLoading: false, loading: true });
  };

  menuItemsArr = {
    1: {
      icon: DocumentsType.EXCEL,
      title: "Arrival Summary",
      action: (data, filters, columns) => this.handlePrint(data, columns, 0),
    },
    2: {
      icon: DocumentsType.EXCEL,
      title: "Producer Arrival Summary",
      action: (data, filters, columns) => this.handlePrint(data, columns, 1),
    },
  };

  handleSave = async () => {
    this.setState({ dataLoading: true });
    try {
      await updateArrivalData(this.state.dataChanged);
      this.context.updateSnack({ show: true, color: "green", message: "Successfully updated data" });
      this.setState({ dataChanged: [] });
      this.props.handleReload();
    } catch (error) {
      const err = GenerateErrorMessage(error, "Failed to update data");
      this.context.updateSnack({ show: true, color: "red", message: err });
    }
    this.setState({ dataLoading: false });
  };

  handleChange = async (property: string, value: any) => {
    // in this case, we only want to update "QC Note(note)" field
    const dirtyRows = value.reduce((arr, item) => {
      if (item.rowDirty && item.rowDirty == 1) {
        arr.push({ arrival_id: item.arrival_id, note: item.note });
      }
      return arr;
    }, []);
    this.setState({ dataChanged: dirtyRows, data: value });
  };

  render() {
    const { classes } = this.state;

    return (
      <div className={classes.root}>
        {this.state.bePatient && (
          <DialogInformation
            isOpen
            showinput={false}
            title={"BE PATIENT"}
            handleOK={this.handleBePatientClose}
            handleClose={this.handleBePatientClose}
            body={"This could take a moment. Please be patient."}
          />
        )}
        {this.state.captureNote && (
          <Confirmation isOpen={true} handleClose={this.handleGridActionClose} handleConfirm={this.handleGridActionClose} title={`Grid Record Action`} body={undefined}>
            <ArrivalGridActionDialog arrivalid={this.state.captureNote} handleClose={this.handleGridActionClose} />
          </Confirmation>
        )}
        <div className={classes.toolbarWrapper}>
          <Toolbar className={classes.toolbar}>
            <RedButton disabled={this.state.dataLoading || this.state.dataChanged.length === 0} variant="contained" onClick={this.handleSave}>
              Save
            </RedButton>
            <FormControlLabel
              control={<Switch checked={this.state.hideCompleted} onChange={this.handleHideCompleted} value="HIDE COMPLETED" color="primary" />}
              label="HIDE COMPLETED"
            />
            {this.state.dataLoading && <CircularProgress size={24} />}
          </Toolbar>
          <Grid
            fill
            rowHeight={30}
            data={this.state.data.filter((item) => (this.state.hideCompleted ? item.arrival_id.toString() == "" || item.arrival_id.toString() == "0" : item))}
            loading={this.props.loading}
            menuItems={this.menuItemsArr}
            clearFilters={"arrivalsgridsetup"}
            GridColumns={this.GridColumnsOverride}
            totalRowColumns={["nocartons", "palletSize"]}
            handleRefresh={() => this.props.handleReload()}
            rowRenderer={(props) => this.rowRenderer(props, classes)}
            handleChange={this.handleChange}
          />
        </div>
      </div>
    );
  }
}

export const ArrivalsGrid = withStyles(styles, { withTheme: true })(ArrivalsGridUnstyled);
